import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import useDebounce from 'hooks/useDebounce';
import { useHistory, useLocation } from 'react-router-dom';
import axios from 'axios';
import { isEqual } from 'lodash';
import moment from 'moment';

import { useStore } from 'components/Store';
import { paths } from 'routes/paths';
import { CROPWISE_PROXY_URL } from 'utilities/apiConstants';
import { getRequestConfig } from '../apiHelpers';
import { OperationSummaryParams, SortConfig } from '../Components/types';
import { useOperationSortContext } from './SortContext';
import { DEFAULT_SORT } from '../Components/hooks/useOperationSort';
import {
  initialValues as defaultFilterValues,
  FilterFormikData
} from '../Components/FilterDrawer/Constants/filterFormikData';

const PAGE_SIZE = 50;

const parsePaginationParam = (
  paginationParam: string | null
): { page: number; size: number } | null => {
  let parsed: { page: number; size: number } | null = null;

  if (!paginationParam) {
    return parsed;
  }

  try {
    parsed = JSON.parse(paginationParam);

    if (
      parsed &&
      typeof parsed.page === 'number' &&
      typeof parsed.size === 'number'
    ) {
      return { page: parsed.page, size: parsed.size };
    }
  } catch {
    throw new Error('Malformed pagination parameter');
  }

  return parsed;
};

const parseJsonParam = <T,>(
  param: string | null,
  errorMessage: string
): T | null => {
  if (!param) {
    return null;
  }

  try {
    return JSON.parse(param);
  } catch {
    throw new Error(errorMessage);
  }
};

interface SummaryParamsContextType {
  params: OperationSummaryParams;
  setParams: React.Dispatch<React.SetStateAction<OperationSummaryParams>>;
  hasSearchTerm: boolean;
  hasFilters: boolean;
  isDrawerVisible: boolean;
  setIsDrawerVisible: React.Dispatch<React.SetStateAction<boolean>>;
  filters: FilterFormikData;
  setFilters: React.Dispatch<React.SetStateAction<FilterFormikData>>;
  handleFiltersChange: (filters: FilterFormikData) => void;
}

const SummaryParamsContext = createContext<
  SummaryParamsContextType | undefined
>(undefined);

export const useSummaryParams = () => {
  const context = useContext(SummaryParamsContext);
  if (!context) {
    throw new Error(
      'useSummaryParams must be used within SummaryParamsProvider'
    );
  }
  return context;
};

export const useSearch = () => {
  const { params, setParams } = useSummaryParams();

  const [searchTerm, setSearchTerm] = useState<string>(
    params?.filters?.name ?? ''
  );
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const history = useHistory();

  const handleSearchChange = useCallback(
    (value: string) => {
      const searchParams = new URLSearchParams(
        globalThis.window.location.search
      );

      setSearchTerm(value);

      // Remove pagination from URL and params when search changes
      searchParams.delete('pagination');
      // Drop the operationId too, so editing/clearing the search doesn't get
      // re-seeded back to the id-selected operation on the next render.
      searchParams.delete('operationId');

      if (value === '') {
        searchParams.delete('search');
        setParams(prevParams => ({
          ...prevParams,
          filters: { name: '' },
          page: 0,
          size: PAGE_SIZE,
          id: undefined
        }));
      } else {
        searchParams.set('search', value);
        setParams(prevParams => ({
          ...prevParams,
          filters: { name: value },
          page: 0,
          size: PAGE_SIZE,
          id: undefined
        }));
      }

      history.replace({ search: searchParams.toString() });
    },
    [history, setParams]
  );

  useEffect(() => {
    if (debouncedSearchTerm) {
      setParams(prevParams => ({
        ...prevParams,
        filters: { name: debouncedSearchTerm }
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm]);

  // On a cold load (Ctrl+Click / refresh) the name is resolved from the URL's
  // operationId after mount, so reflect that resolved value in the search box.
  // Guarded on an empty box so it never overwrites what the user is typing.
  useEffect(() => {
    const resolvedName = params?.filters?.name ?? '';
    if (resolvedName && !searchTerm) {
      setSearchTerm(resolvedName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params?.filters?.name]);

  return { handleSearchChange, searchTerm };
};

export const usePagination = ({
  currentPage,
  totalElements,
  pageSize
}: {
  currentPage: number;
  totalElements: number;
  pageSize: number;
}) => {
  const { params, setParams, hasFilters, hasSearchTerm } = useSummaryParams();
  const history = useHistory();

  const [pagination, setPagination] = useState<{
    page: number;
    size: number;
  }>({
    page: params.page ?? 0,
    size: params.size ?? PAGE_SIZE
  });

  const setParamsOnURL = useCallback(
    (newPagination: { page: number; size: number }) => {
      const searchParams = new URLSearchParams(
        globalThis.window.location.search
      );

      if (newPagination.page === 0 && newPagination.size === PAGE_SIZE) {
        searchParams.delete('pagination');
      } else {
        searchParams.set('pagination', JSON.stringify(newPagination));
      }

      history.replace({ search: searchParams.toString() });
    },
    [history]
  );

  const handlePaginationChange = useCallback(
    (page: number, size: number) => {
      const targetPage = page - 1;
      setPagination({ page, size });
      setParams(prevParams => ({
        ...prevParams,
        page: targetPage,
        size
      }));
      setParamsOnURL({ page, size });
    },
    [setParams, setParamsOnURL]
  );

  const config = useMemo(
    () => ({
      current: currentPage + 1,
      total: totalElements,
      pageSize,
      showTotal: (total: number, range: number[]) =>
        `${range[0]}-${range[1]} of ${total}${
          hasFilters || hasSearchTerm ? ' results' : ''
        }`,
      showSizeChanger: false,
      showQuickJumper: false,
      size: 'default' as const,
      onChange: handlePaginationChange
    }),
    [
      currentPage,
      totalElements,
      pageSize,
      handlePaginationChange,
      hasFilters,
      hasSearchTerm
    ]
  );

  return { pagination, setPagination, config };
};

export const useSort = ({
  onSortChange
}: { onSortChange?: () => void } = {}) => {
  const { setParams } = useSummaryParams();
  const { sorts, setSort } = useOperationSortContext();
  const previousSortRef = useRef<string>('');
  const history = useHistory();

  useEffect(() => {
    const searchParams = new URLSearchParams(globalThis.window.location.search);
    // Check if sort has changed and reset pagination if so
    const currentSortString = JSON.stringify(sorts);
    const sortChanged = previousSortRef.current !== currentSortString;

    // Update previous sort values
    previousSortRef.current = currentSortString;

    if (sortChanged) {
      setParams(prevParams => ({
        ...prevParams,
        page: 0,
        size: PAGE_SIZE,
        sorts
      }));

      if (isEqual(sorts, DEFAULT_SORT)) {
        searchParams.delete('sorts');
      } else {
        searchParams.set('sorts', currentSortString);
      }

      history.replace({ search: searchParams.toString() });

      onSortChange?.();
    }
  }, [sorts, setParams, previousSortRef, history, onSortChange]);

  return { sorts, setSort };
};

const buildSerializableFilters = (
  filters: FilterFormikData
): Partial<FilterFormikData> => ({
  dateRange: filters.dateRange,
  cropZoneIds: filters.cropZoneIds,
  matchType: filters.matchType
});

export const useFilters = ({
  setParams,
  params
}: {
  setParams: React.Dispatch<React.SetStateAction<OperationSummaryParams>>;
  params: OperationSummaryParams;
}) => {
  const initialFilters: FilterFormikData = {
    ...defaultFilterValues,
    ...(params.fromDate && params.toDate
      ? {
          dateRange: [moment(params.fromDate), moment(params.toDate)] as [
            moment.Moment,
            moment.Moment
          ]
        }
      : {}),
    ...(params.cropZoneIds?.length ? { cropZoneIds: params.cropZoneIds } : {}),
    ...(params.cropZoneMatchType ? { matchType: params.cropZoneMatchType } : {})
  };

  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [filters, setFilters] = useState<FilterFormikData>(initialFilters);
  const history = useHistory();

  const handleFiltersChange = useCallback(
    (newFilters: FilterFormikData) => {
      const searchParams = new URLSearchParams(
        globalThis.window.location.search
      );

      const { dateRange, cropZoneIds, matchType } = newFilters;

      setParams(prevParams => ({
        ...prevParams,
        id: undefined,
        fromDate: dateRange?.[0]?.format(),
        toDate: dateRange?.[1]?.format(),
        cropZoneIds: cropZoneIds?.length ? cropZoneIds : undefined,
        cropZoneMatchType: cropZoneIds?.length ? matchType : undefined
      }));

      const serializableFilters = buildSerializableFilters(newFilters);
      const isDefaultFilters = isEqual(newFilters, defaultFilterValues);

      if (isDefaultFilters) {
        searchParams.delete('filters');
      } else {
        searchParams.set('filters', JSON.stringify(serializableFilters));
      }

      history.replace({ search: searchParams.toString() });

      setFilters(newFilters);
    },
    [history, setParams]
  );

  return {
    isDrawerVisible,
    setIsDrawerVisible,
    filters,
    setFilters,
    handleFiltersChange
  };
};

export const SummaryParamsProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const searchParams = new URLSearchParams(globalThis.window.location.search);
  const {
    state: { loadingOrg, organization }
  } = useStore();

  const history = useHistory();
  const location = useLocation();
  // Ctrl+Click / open-in-new-tab drops the in-memory router state, so the id
  // also travels as an `operationId` query param; fall back to it so the
  // specific operation is still selected by id (not just by the ambiguous name).
  const operationId =
    (location.state as { operationId?: string })?.operationId ??
    searchParams.get('operationId') ??
    undefined;
  const name = searchParams.get('search') ?? '';

  const pendingOperationIdRef = useRef<string | undefined>(undefined);
  if (name) {
    pendingOperationIdRef.current = undefined;
  } else if (operationId) {
    pendingOperationIdRef.current = operationId;
  }

  let pagination: { page: number; size: number } | null = null;

  const paginationParam = searchParams.get('pagination');
  const sortsParam = searchParams.get('sorts');
  const filtersParam = searchParams.get('filters');

  const parsedFilters = parseJsonParam<FilterFormikData>(
    filtersParam,
    'Malformed filters parameter'
  );

  const sorts =
    parseJsonParam<SortConfig[]>(sortsParam, 'Malformed sorts parameter') ??
    null;

  const fromDate = parsedFilters?.dateRange?.[0]
    ? moment(parsedFilters.dateRange[0]).format()
    : undefined;

  const toDate = parsedFilters?.dateRange?.[1]
    ? moment(parsedFilters.dateRange[1]).format()
    : undefined;

  const cropZoneIds = parsedFilters?.cropZoneIds?.length
    ? parsedFilters.cropZoneIds
    : undefined;

  const cropZoneMatchType = parsedFilters?.matchType ?? undefined;

  pagination = parsePaginationParam(paginationParam);

  const [params, setParams] = useState<OperationSummaryParams>({
    ...(pagination ? { page: pagination.page, size: pagination.size } : {}),
    ...(sorts ? { sorts } : {}),
    ...(fromDate ? { fromDate } : {}),
    ...(toDate ? { toDate } : {}),
    ...(cropZoneIds ? { cropZoneIds } : {}),
    ...(cropZoneMatchType ? { cropZoneMatchType } : {}),
    filters: { name },
    ...(operationId ? { id: operationId } : {})
  });

  const {
    isDrawerVisible,
    setIsDrawerVisible,
    filters,
    setFilters,
    handleFiltersChange
  } = useFilters({ setParams, params });

  const hasSearchTerm = params.filters?.name !== '';
  const hasFilters =
    (params.fromDate !== undefined && params.toDate !== undefined) ||
    (params.cropZoneIds !== undefined && params.cropZoneIds.length > 0);

  const value = useMemo(
    () => ({
      params,
      setParams,
      hasSearchTerm,
      hasFilters,
      isDrawerVisible,
      setIsDrawerVisible,
      filters,
      setFilters,
      handleFiltersChange
    }),
    [
      params,
      setParams,
      hasFilters,
      hasSearchTerm,
      isDrawerVisible,
      setIsDrawerVisible,
      filters,
      setFilters,
      handleFiltersChange
    ]
  );

  // Clear the search on an org SWITCH (the org was already loaded and is now
  // reloading), but NOT on the initial cold load — there the search comes from
  // the URL (Ctrl+Click / shared link) and must survive without a re-fetch,
  // otherwise the name only reappears via a racy re-resolve of the id.
  const orgHasLoadedRef = useRef(false);
  useEffect(() => {
    if (!loadingOrg) {
      orgHasLoadedRef.current = true;
      return;
    }
    if (orgHasLoadedRef.current && params.filters?.name) {
      searchParams.delete('search');
      history.replace({ search: searchParams.toString() });
      setParams(prevParams => ({ ...prevParams, filters: { name: '' } }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingOrg, params.filters?.name]);

  // Resolve operation name by id when only operationId is provided.
  useEffect(() => {
    const resolveOperationName = async () => {
      const pendingId = pendingOperationIdRef.current;
      if (!pendingId || name || !organization?.id) {
        return;
      }
      // Clear early to prevent duplicate resolution if deps change mid-fetch.
      pendingOperationIdRef.current = undefined;

      const isApplicationsPath = location.pathname.startsWith(
        paths.applications
      );
      const isWorkOrdersPath = location.pathname.startsWith(paths.workOrders);

      if (!isApplicationsPath && !isWorkOrdersPath) {
        pendingOperationIdRef.current = pendingId; // restore so name resolution can still be retried on a relevant page
        return;
      }

      const operationPath = isApplicationsPath ? 'applications' : 'work-orders';

      try {
        const { data } = await axios.get<{ name?: string }>(
          `${CROPWISE_PROXY_URL}/v1/base/${operationPath}/${pendingId}`,
          getRequestConfig(organization.id)
        );

        if (!data?.name) {
          return;
        }

        const currentSearchParams = new URLSearchParams(location.search);
        currentSearchParams.delete('operationId');
        currentSearchParams.set('search', data.name);

        setParams(prevParams => ({
          ...prevParams,
          filters: {
            ...prevParams.filters,
            name: data.name
          }
        }));

        history.replace({
          ...location,
          search: currentSearchParams.toString(),
          state: {
            ...(location.state as { operationId?: string }),
            operationId: undefined
          }
        });
      } catch {
        // Keep id-based flow even if name resolution fails.
      }
    };

    resolveOperationName();
    // Depend on pathname/search (not the whole location) so a state-only change
    // (clearing operationId from router state) doesn't re-trigger resolution.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, location.pathname, location.search, name, organization?.id]);

  // Clear the operationId from the in-memory router state once consumed. The
  // query-param copy (used on a new-tab cold load) is intentionally left for
  // resolveOperationName to consume and strip after it resolves the name, so the
  // search box still gets populated.
  useEffect(() => {
    const stateOperationId = (location.state as { operationId?: string })
      ?.operationId;
    if (!stateOperationId) {
      return;
    }
    history.replace({
      ...location,
      state: {
        ...(location.state as { operationId?: string }),
        operationId: undefined
      }
    });
  }, [location, history]);

  return (
    <SummaryParamsContext.Provider value={value}>
      {children}
    </SummaryParamsContext.Provider>
  );
};
